/**
 * HelpFlowDiagram.tsx — the "How it all connects" chain diagram of the
 * page-help dialog. Pure HTML/CSS (flexbox) built from theme tokens — no
 * chart library, no hex literals.
 *
 * Stages render left-to-right joined by arrows (wrapping on small screens);
 * each stage stacks its nodes vertically. Nodes with a `route` are clickable
 * chips that navigate there (the dialog closes first — handled by the
 * caller's onNavigate).
 */

import { Box, Chip, Typography, alpha, useTheme } from '@mui/material';
import { ArrowForward, Launch } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import { Fragment } from 'react';
import type { HelpFlowStage, HelpFlowTone } from './helpTypes';

/** Tone → theme token (keeps content files free of color decisions). */
export const flowToneColor = (tone: HelpFlowTone, theme: Theme): string => {
  switch (tone) {
    case 'sage': return theme.palette.primary.main;
    case 'machine': return theme.schedule.machineTime;
    case 'skill': return theme.schedule.employeeAlloc;
    case 'quote': return theme.palette.secondary.main;
    case 'attention': return theme.admin.mappingUnconfirmed;
    case 'neutral': return theme.admin.mappingNameMatch;
  }
};

interface HelpFlowDiagramProps {
  stages: HelpFlowStage[];
  onNavigate: (route: string) => void;
}

export const HelpFlowDiagram = ({ stages, onNavigate }: HelpFlowDiagramProps) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
        gap: 1,
        rowGap: 2,
      }}
    >
      {stages.map((stage, idx) => (
        <Fragment key={stage.title}>
          {idx > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <ArrowForward sx={{ color: alpha(theme.palette.text.secondary, 0.6), fontSize: 20 }} />
            </Box>
          )}
          <Box
            sx={{
              flex: '1 1 130px',
              minWidth: 130,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              p: 1,
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.text.secondary, 0.3)}`,
              backgroundColor: alpha(theme.palette.background.default, 0.6),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: theme.palette.text.secondary,
              }}
            >
              {stage.title}
            </Typography>
            {stage.nodes.map((node) => {
              const color = flowToneColor(node.tone, theme);
              return (
                <Box key={node.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Chip
                    label={node.label}
                    size="small"
                    clickable={!!node.route}
                    onClick={node.route ? () => onNavigate(node.route as string) : undefined}
                    icon={node.route ? <Launch sx={{ fontSize: 14 }} /> : undefined}
                    sx={{
                      justifyContent: 'flex-start',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      height: 26,
                      color,
                      backgroundColor: alpha(color, 0.12),
                      border: `1px solid ${alpha(color, 0.5)}`,
                      '& .MuiChip-icon': { color },
                      ...(node.route && {
                        '&:hover': { backgroundColor: alpha(color, 0.22) },
                      }),
                    }}
                  />
                  {node.sublabel && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, lineHeight: 1.3 }}>
                      {node.sublabel}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
};
